import {
    app,
    Component,
    ojs,
    component,
    payload,
} from "modular-openscriptjs";
import { empty, isMobile, pageInfo } from "../../../helpers";
import { api } from "../../../declarations";
import SearchResultProductView from "../../Views/SearchResultProductView";
import Spinner from "../../Blocks/Spinner";
import InfiniteScroll from "../../Blocks/InfiniteScroll";
import { dom } from "../../../fotastart";

const h = app("h");
const router = app("router");
const broker = app("broker");

ojs(SearchResultProductView, InfiniteScroll, Spinner);

/**
 * One page, several backend routes.
 *
 * This page renders the same "grid of a business's products, paginated
 * with infinite scroll" shell for more than one destination - "Featured
 * Products" and "Top Sellers" today, more later - without duplicating
 * that shell per feature the way BusinessProductsSearchResult /
 * CategoryProductsResult / SubcategoryProductsResult each do for their
 * own single purpose. Instead the page asks the router which named route
 * it was reached through (router.is(...), same technique
 * dc.searchHandlingRoutes/VoiceOrderCall.js already use elsewhere) and
 * looks up the endpoint + title for that route here.
 *
 * To add a new mode: register a router.on(...) for it in ojs.routes.js
 * (rendering h.BusinessProductListingPage(), same as the two below), then
 * add one entry to MODES keyed by that route's name.
 */
const MODES = {
    "business.products.featured": {
        title: "Featured Products",
        endpoint: (businessId) => `/companies/${businessId}/products/featured`,
        emptyMessage: "This business hasn't featured any products yet.",
    },
    "business.products.top-sellers": {
        title: "Top Sellers",
        endpoint: (businessId) => `/companies/${businessId}/products/top-sellers`,
        emptyMessage: "No top sellers to show yet.",
    },
    "business.products.deal": {
        title: "Deal Products",
        endpoint: (businessId, extra) =>
            `/companies/${businessId}/deals/${extra?.dealId}/products`,
        emptyMessage: "No products in this deal right now.",
    },
};

export default class BusinessProductListingPage extends Component {
    businessId = null;
    dealId = null;
    routeName = null;
    mode = null;
    title = null;

    pageMap = { next: 1, total: 0 };

    resolveMode() {
        for (let routeName in MODES) {
            if (router.is(routeName)) {
                return { routeName, mode: MODES[routeName] };
            }
        }
        return { routeName: null, mode: null };
    }

    init() {
        this.businessId = router.params.businessId;
        this.dealId = router.params.dealId || null;
        let resolved = this.resolveMode();
        this.routeName = resolved.routeName;
        this.mode = resolved.mode;
        this.title = this.mode?.title || null;
        this.pageMap = { next: 1, total: 0 };

        if (this.mode) {
            pageInfo(this.title);
        }
    }

    render(...args) {
        let params = router.params || {};
        let { routeName } = this.resolveMode();

        // router.is(...) only reflects the *current* route once the
        // router has actually navigated there, so re-run init() whenever
        // either the businessId param or the resolved mode/route changes
        // (covers both "featured -> top-sellers for the same business"
        // and "same mode, different business" navigations).
        if (
            this.businessId !== params.businessId ||
            this.dealId !== (params.dealId || null) ||
            this.routeName !== routeName
        ) {
            this.init();
        }

        if (!this.mode) {
            return h.div(
                { class: "container px-4 py-5 text-center" },
                h.h3({ class: "fw-bold text-dark" }, "Page Not Found"),
                ...args,
            );
        }

        return h.div(
            { class: "pt-3 pb-5 container mb-5 py-md-5" },
            h.div(
                { class: "d-flex align-items-center gap-2 mb-4 px-1" },
                h.span(
                    {
                        class: "btn btn-light rounded-circle d-flex align-items-center justify-content-center p-0",
                        style: "width: 40px; height: 40px;",
                        role: "button",
                        listeners: {
                            click: () => window.history.back(),
                        },
                    },
                    h.i({ class: "bi bi-arrow-left fs-5" }),
                ),
                h.h4(
                    { class: "fw-bold m-0", id: "c-business-listing-title" },
                    this.title,
                ),
            ),
            h.InfiniteScroll({
                child: h.div({
                    id: "c-business-products-listing",
                    class: "d-flex gap-3 gap-md-4 flex-wrap justify-content-around justify-content-md-start",
                }),
                callback: (entries, observer, marker) => {
                    entries.forEach((entry) => {
                        if (!entry.isIntersecting) return;
                        this.getProducts("c-business-products-listing", marker);
                    });
                },
            }),
            ...args,
        );
    }

    $_mounted(componentId) {
        let c = component(componentId);
        if (!c || !c.mode) return;
        c.getProducts("c-business-products-listing");
    }

    async getProducts(holderId, spinnerHolder = null) {
        if (!this.mode) return;

        let page = this.pageMap.next;

        let spinner = null;
        if (spinnerHolder) {
            spinner = h.Spinner({ parent: spinnerHolder, resetParent: true });
        }

        try {
            let res = await api.get(
                this.mode.endpoint(this.businessId, { dealId: this.dealId }),
                { page, interval: 10 },
                { silent: true },
            );

            if (res) {
                let products = res.data.products.data || [];
                let currentPage = res.data.products.current_page;
                this.pageMap.total = res.data.products.total ?? 0;

                if (
                    this.routeName === "business.products.deal" &&
                    res.data.deal?.name
                ) {
                    let newTitle = `Deal: ${res.data.deal.name}`;
                    if (this.title !== newTitle) {
                        this.title = newTitle;
                        pageInfo(this.title);
                        const titleEl = dom.id("c-business-listing-title");
                        if (titleEl) titleEl.textContent = this.title;
                    }
                }

                const holder = dom.id(holderId);
                if (holder && currentPage === 1) {
                    dom.empty(holder);
                }

                for (let product of products) {
                    if (
                        holder &&
                        !dom.get(`[feed-product-id="p-${product.id}"]`, holder)
                    ) {
                        h.SearchResultProductView(product, {
                            class: "flex-shrink-0 h-100",
                            style: isMobile() ? "width: 150px;" : "width: 280px;",
                            parent: holder,
                            $feed_product_id: `p-${product.id}`,
                            listeners: {
                                click: () => {
                                    broker.send(
                                        app("$e").product.needs.display,
                                        payload({ product }),
                                    );
                                },
                            },
                        });
                    }
                }

                if (empty(products) && currentPage === 1) {
                    this._showEmpty(holderId, this.mode.emptyMessage);
                }

                let lastPageNum = res.data.products.last_page;
                if (!empty(products) && currentPage < lastPageNum) {
                    this.pageMap.next = currentPage + 1;
                } else {
                    this.pageMap.next = currentPage;
                }
            }
        } catch (e) {
            console.error(e);
        } finally {
            spinner?.remove();
        }
    }

    _showEmpty(holderId, message) {
        const holder = dom.id(holderId);
        if (!holder) return;

        h.div(
            {
                parent: holder,
                resetParent: true,
                class: "card border-0 rounded-4 w-100 p-5 text-center bg-white shadow-sm border border-light",
            },
            h.div(
                {
                    class: "text-muted d-flex flex-column align-items-center justify-content-center gap-3",
                },
                h.i({ class: "bi bi-bag fs-1 text-secondary opacity-50" }),
                h.h5({ class: "fw-bold text-dark mb-0" }, message),
            ),
        );
    }
}

ojs(BusinessProductListingPage);
